import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTestAuthBypass } from './test-helpers/auth-bypass.js';
import { ConfirmProvider } from './components/boothly/confirm-provider.jsx';
import { useToast } from './components/boothly/toast-provider.jsx';
import { DemoShadcn } from './components/demo-shadcn.jsx';
import { useSwipeBack } from './use-swipe-back.js';
import { supabase } from './supabase.js';
import { F, T, Icon } from './theme.jsx';
import { useModalViewport, modalShellStyle, ModalHeader } from './modal-utils.jsx';
import { syncManager } from './sync.jsx';
import { Troedel } from './flea/v2';
import { AppShell } from './global-nav/app-shell';
import { resetSheetCount } from './widgets.jsx';
// GlobalSettingsSheet removed — settings consolidated into ProfilTab
import { OnlineStore } from './online/v2/index.js';
import { AuthScreen, ModeHubStub, CreateStoreScreen, JoinStoreScreen } from './landing/auth-screens.jsx';
import { StaleSessionReminderSheet, CreateAreaSheet } from './landing/area-sheets.jsx';

const API_URL = "/api/";


// ── Role-permission map ───────────────────────────────────────
const ROLE_PERMS = {
  owner:       ["read","write","delete","finance","manage_members","manage_roles","manage_store"],
  admin:       ["read","write","delete","finance","manage_members"],
  fulfillment: ["read","write"],
};

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error(`ErrorBoundary [${this.props.fallbackLabel || "App"}] caught:`, error, info);
  }
  render() {
    if (this.state.error) {
      const label = this.props.fallbackLabel || "UI";
      const onReset = this.props.onReset;
      return React.createElement("div", {
        style: { padding: 24, fontFamily: "system-ui", color: "#fff", background: "#1a1a1a", minHeight: "100vh" }
      },
        React.createElement("h2", { style: { color: "#FF453A" } }, `Fehler im ${label}`),
        React.createElement("pre", { style: { fontSize: 11, whiteSpace: "pre-wrap", color: "#FF9F0A" } }, String(this.state.error?.stack || this.state.error)),
        React.createElement("button", {
          onClick: () => { this.setState({ error: null }); if (onReset) onReset(); },
          style: { marginTop: 16, padding: "10px 20px", borderRadius: 8, border: "none", background: "#378ADD", color: "#fff", fontSize: 14, cursor: "pointer" }
        }, "Zurück zur Übersicht")
      );
    }
    return this.props.children;
  }
}

const STORAGE_KEY  = "memorica-orders";
const DARKMODE_KEY = "memorica-darkmode";

// ── Auth-Confirm-Redirect-Snapshot ──────────────────────────────
// Supabase (detectSessionInUrl=true) parst den Bestaetigungs-Hash beim Laden
// ASYNCHRON und strippt ihn danach aus der URL. Deshalb MUESSEN wir die
// Parameter synchron beim Modul-Load lesen — dieser Code laeuft vor dem
// async-Init-Microtask von supabase-js, der Hash ist hier also noch intakt.
// Fehler-Params (abgelaufener Link) kommen im Implicit-Flow ebenfalls im Hash.
const CONFIRM_REDIRECT = (() => {
  try {
    if (typeof window === "undefined") return null;
    const raw = (window.location.hash || "").replace(/^#/, "")
      + "&" + (window.location.search || "").replace(/^\?/, "");
    const params = new URLSearchParams(raw);
    const error = params.get("error") || params.get("error_code");
    if (error) return { kind: "error" };
    if (params.get("type") === "signup") return { kind: "confirmed" };
    return null;
  } catch { return null; }
})();

// E-Mails die automatisch in den Helfer-Modus gezwungen werden
// (egal was im persistenten State steht)
const HELPER_EMAILS = ["md@recout.de"];

const STATUS = {
  "Offen":     { color: "var(--accent-online)", bg: "rgba(55,138,221,0.15)" },
  "Bereit":    { color: "var(--accent-troedel)", bg: "rgba(127,119,221,0.15)" },
  "Versendet": { color: "var(--accent-stand)", bg: "rgba(48,209,88,0.15)"  },
  "Retoure":   { color: "var(--accent-red)", bg: "rgba(255,69,58,0.15)"  },
};
const STATUS_ORDER = ["Offen", "Bereit", "Versendet", "Retoure"];
const TABS    = ["Alle", "Bereit", "Versendet", "Retoure"];
// KANAELE constant removed — channels are now per-store via getAllChannels() / getActiveChannels()

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = (k = "etsy") => ({ bestellnummer: "", kanal: k, datum: today(), status: "Offen", notiz: "", trackingNumber: "", retoureErhalten: false, adresse: "", labelKey: "", labelMime: "" });
const ageInDays = (d) => { if (!d) return 0; const then = new Date(d); const now = new Date(); return Math.floor((now - then) / 86400000); };
const ageLabel = (d) => {
  const n = ageInDays(d);
  if (n <= 0) return "heute";
  if (n === 1) return "gestern";
  return `vor ${n} Tagen`;
};

// Builds the label base URL: store-scoped when storeId+token given, legacy fallback otherwise.

// Responsive layout breakpoints
function useViewport() {
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 430);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  const isMobile  = vw < 768;
  const isTablet  = vw >= 768 && vw < 1024;
  const isDesktop = vw >= 1024;
  // Width of the list column (left side on desktop, full width on mobile/tablet)
  const listW = isMobile ? 430 : isTablet ? 560 : 440;
  // Total container max width
  const containerMaxW = isDesktop ? 1280 : listW;
  return { vw, isMobile, isTablet, isDesktop, listW, containerMaxW };
}



// ─────────────────────────────────────────────────────────────
// Main App
// ─────────────────────────────────────────────────────────────
function App() {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('demo') === 'shadcn') {
    return <DemoShadcn />;
  }

  const { vw, isMobile, isTablet, isDesktop, listW, containerMaxW } = useViewport();
  const { height: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop } = useModalViewport();

  // ── Auth & multi-store state ──────────────────────────────
  const [appScreen, setAppScreen]         = useState("loading");
  const [prevAppScreen, setPrevAppScreen] = useState(null);
  const [authToken, setAuthToken]         = useState(null);
  const [authUser, setAuthUser]           = useState(null);
  const authTokenRef = useRef(null);
  useEffect(() => { authTokenRef.current = authToken; }, [authToken]);
  useEffect(() => {
    const sm = syncManager;
    if (!sm) return;
    if (authToken) {
      sm.setTokenGetter(() => authTokenRef.current);
      sm.init().catch(() => {});
    } else {
      sm.reset().catch(() => {});
    }
  // eslint-disable-next-line
  }, [!!authToken]);
  const [userStores, setUserStores]       = useState([]);
  // Letzten Bereich gelöscht → zurück aufs Dashboard. Ein aus dem Lösch-Sheet
  // verwaister Sheet-Open-Zähler würde die BottomNav verstecken → hart zurücksetzen.
  useEffect(() => { if (userStores.length === 0) resetSheetCount(); }, [userStores.length]);
  const [currentStore, setCurrentStore]   = useState(null);
  const [currentRole, setCurrentRole]     = useState(null);
  const [userPreferences, setUserPreferences] = useState({});
  const [landingData, setLandingData] = useState({
    byStore: {}, weekRevenue: { online: 0, flea: 0 },
  });
  const [createAreaOpen, setCreateAreaOpen] = useState(false);
  const [loadingLanding, setLoadingLanding] = useState(false);
  const [staleSessionDismissed, setStaleSessionDismissed] = useState(() => new Set());
  const [staleEndTarget, setStaleEndTarget] = useState(null);
  const [currentMode, setCurrentMode]     = useState("online");
  const [activeSessions, setActiveSessions] = useState({});
  // Deep-Link: Dashboard-Live-Card → Trödel direkt in der Live-Markt-Ansicht öffnen
  const [openLiveDirectly, setOpenLiveDirectly] = useState(false);
  // ── Theme (3-stufig: light / dark / system) ──────────────────────────────────
  const [themeMode, setThemeMode] = useState(() => {
    try {
      const stored = localStorage.getItem('boothly_theme');
      if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
      // Migration von memorica_dark
      const legacy = localStorage.getItem('memorica_dark');
      if (legacy === '1') return 'dark';
      if (legacy === '0') return 'light';
    } catch {}
    return 'system';
  });
  const [systemDark, setSystemDark] = useState(() => {
    try { return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false; } catch { return false; }
  });
  useEffect(() => {
    if (themeMode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeMode]);
  const dark = themeMode === 'dark' || (themeMode === 'system' && systemDark);
  useEffect(() => {
    localStorage.setItem('boothly_theme', themeMode);
    document.documentElement.classList.toggle('dark', dark);
  }, [themeMode, dark]);
  const t = dark ? T.dark : T.light;

  const loadStoresRequestRef = useRef(0);
  const toast = useToast();

  // ── Bestaetigungs-Feedback nach E-Mail-Confirmation ──────────
  // Einmalig pro Page-Load: Erfolgs-Toast nach Klick auf den Signup-Link
  // (Fall A — User ist danach eingeloggt), bzw. Fehler-Toast bei abgelaufenem
  // /ungueltigem Link. URL danach saeubern, damit ein Reload nicht erneut feuert.
  const confirmFeedbackFired = useRef(false);
  useEffect(() => {
    if (confirmFeedbackFired.current || !CONFIRM_REDIRECT) return;
    confirmFeedbackFired.current = true;
    try { window.history.replaceState(null, "", window.location.pathname); } catch {}
    if (CONFIRM_REDIRECT.kind === "error") {
      toast.error("Der Bestätigungslink ist abgelaufen oder ungültig. Fordere einen neuen an.");
    } else {
      toast.success("E-Mail bestätigt – willkommen bei Boothly");
    }
    // eslint-disable-next-line
  }, []);

  // ── Navigation helpers ────────────────────────────────────
  const navigate = useCallback((screen) => {
    setPrevAppScreen(prev => (prev === screen ? prev : appScreen));
    setAppScreen(screen);
  }, [appScreen]);

  const goBack = useCallback(() => {
    if (prevAppScreen) {
      setAppScreen(prevAppScreen);
      setPrevAppScreen(null);
    }
  }, [prevAppScreen]);

  const canSwipeBack = ['app', 'flea-hub', 'create-store', 'join-store'].includes(appScreen);
  const { swipeRef, swipeStyle } = useSwipeBack({ onBack: goBack, enabled: canSwipeBack });

  // ── Supabase auth init ────────────────────────────────────
  // On mount: check for an existing session. If found, load user's stores.
  // Also subscribe to auth state changes (token refresh, sign-out).
  useEffect(() => {
    // Test-bypass — DEV only, tree-shaken in production by Vite
    const testAuth = getTestAuthBypass();
    if (testAuth?.useApi) {
      // Demo-Build: echter Lade-Pfad gegen das In-Page-Mock-Backend (Landing-Daten etc.)
      setAuthUser(testAuth.user);
      setAuthToken('demo-token');
      loadUserStores('demo-token');
      return;
    }
    if (testAuth) {
      setAuthUser(testAuth.user);
      setAuthToken('test-token-bypass');
      setUserStores(testAuth.stores || []);
      setAppScreen('dashboard');
      return;
    }

    if (!supabase) {
      // Supabase not yet configured (REPLACE_ME placeholders) → skip to app directly
      // so existing Cloudflare Access users are not locked out during migration.
      setAppScreen("app");
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setAuthToken(session.access_token);
        setAuthUser(session.user);
        loadUserStores(session.access_token);
      } else {
        setAppScreen("auth");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        setUserStores([]);
        setUserPreferences({});
        setCurrentStore(null);
        setCurrentRole(null);
        setCurrentMode("online");
        setAuthToken(null);
        setAuthUser(null);
        setAppScreen("auth");
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        setAuthToken(session.access_token);
        setAuthUser(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadUserStores(token) {
    const requestId = ++loadStoresRequestRef.current;
    try {
      // Stores + Preferences parallel laden — beides nötig für Auto-Forward-Decision.
      const [storesRes, prefsRes] = await Promise.all([
        fetch("/api/stores", { headers: { "Authorization": `Bearer ${token}` } }),
        fetch("/api/preferences", { headers: { "Authorization": `Bearer ${token}` } }),
      ]);
      if (requestId !== loadStoresRequestRef.current) return; // superseded by newer call
      if (!storesRes.ok) { setAppScreen("auth"); return; }
      const stores = await storesRes.json();
      const prefs = prefsRes.ok ? (await prefsRes.json()).preferences || {} : {};
      setUserStores(stores);
      setUserPreferences(prefs);

      // Auto-Forward-Logik (Block 1):
      //   genau 1 Store + always_show_landing nicht aktiv → direkt rein
      //   sonst → Landingpage
      // Active-Sessions für flea-Stores werden parallel weitergeladen
      // (auch beim Auto-Forward-Pfad, da der Store vielleicht eine aktive
      // Session hat die wir später anzeigen wollen).
      loadActiveSessions(stores, token);
      // Landing-Aggregat-Daten parallel laden (Counts, weekRevenue, recentActivity)
      loadLandingData(stores, token);

      const wantsDashboard = prefs.always_show_landing === true;
      if (!wantsDashboard) {
        const startId = prefs.start_store_id;
        const target = startId ? stores.find(s => s.id === startId) : null;
        if (target) { selectStore(target, token); setPrevAppScreen("dashboard"); return; }
        if (stores.length === 1) { selectStore(stores[0], token); setPrevAppScreen("dashboard"); return; }
      }
      setAppScreen("dashboard");
    } catch (e) {
      console.warn("loadUserStores failed:", e);
      setAppScreen("dashboard");
    }
  }

  // Speichert User-Preferences via Worker (PATCH-Merge).
  // Lokaler State wird optimistisch sofort aktualisiert.
  async function saveUserPreferences(patch) {
    const next = { ...userPreferences, ...patch };
    setUserPreferences(next); // optimistic
    try {
      const res = await fetch("/api/preferences", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        // Revert wenn fehlgeschlagen
        setUserPreferences(userPreferences);
        toast.error("Einstellung konnte nicht gespeichert werden");
      } else {
        const data = await res.json();
        if (data?.preferences) setUserPreferences(data.preferences);
      }
    } catch (e) {
      setUserPreferences(userPreferences);
      toast.error("Netzwerkfehler beim Speichern");
    }
  }

  // ─────────────────────────────────────────────────────────────
  // loadLandingData (P-Landing-Redesign — schlanker)
  // Lädt nur was die neue Landingpage braucht:
  //   • weekRevenue.{online, flea}  → Hero
  //   • byStore[id]: { ordersOpen, sessionsCount, totalRevenue, lastSession }
  //                                          → Bereiche-Liste-Subtexte
  // Kein recentActivity, kein topItem. Bei flea ein einziger
  // Sessions-Call mit ?include_stats=1 statt N+1 Detail-Calls.
  // ─────────────────────────────────────────────────────────────
  async function loadLandingData(stores, token) {
    const t = token || authToken;
    if (!t || !stores || stores.length === 0) {
      setLandingData({ byStore: {}, weekRevenue: { online: 0, flea: 0 }, weeks: [] });
      setLoadingLanding(false);
      return;
    }
    setLoadingLanding(true);
    // Wochenfenster Mo 00:00 → jetzt
    const now = new Date();
    const day = (now.getDay() + 6) % 7; // Mo=0 ... So=6
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - day);
    weekStart.setHours(0, 0, 0, 0);
    const weekStartIso = weekStart.toISOString();

    const headers = { "Authorization": `Bearer ${t}` };
    const byStore = {};
    const weekRevenue = { online: 0, flea: 0 };

    // Wachstumsrate: zwei 30-Tage-Fenster. last30 = [win30, now], prev30 = [win60, win30).
    const DAY_MS = 86400000, nowMs = now.getTime();
    const win30Ms = nowMs - 30 * DAY_MS;
    const win60Ms = nowMs - 60 * DAY_MS;
    // pct + Grund ('ok' | 'new' | 'insufficient') — die zwei null-Fälle intern getrennt halten.
    const growth = (last30, prev30, insufficient) => {
      if (insufficient) return { pct: null, reason: 'insufficient' };
      if (prev30 <= 0) return { pct: null, reason: 'new' };
      return { pct: Math.round((last30 - prev30) / prev30 * 100), reason: 'ok' };
    };

    // 4-Wochen-Zeitreihe für den Überblick-Chart (idx 0 = vor 3W … idx 3 = diese Woche).
    const WEEK_MS = 7 * 86400000;
    const weekStartMs = weekStart.getTime();
    const weeks = [0, 1, 2, 3].map(() => ({ online: 0, flea: 0 }));
    const weekBucket = (ms) => {
      if (!Number.isFinite(ms)) return -1;
      if (ms >= weekStartMs) return 3;                 // laufende Woche
      const back = Math.floor((weekStartMs - ms) / WEEK_MS);
      const idx = 2 - back;                            // back 0→vor1W(2) … 2→vor3W(0)
      return idx >= 0 ? idx : -1;                      // älter als 4 Wochen → ignorieren
    };

    await Promise.all(stores.map(async (s) => {
      const apiBase = `/api/stores/${s.id}`;
      const data = { store: s };
      try {
        if ((s.type || "online") === "online") {
          const r = await fetch(`${apiBase}/orders?limit=1000`, { headers });
          if (r.ok) {
            // Orders-Endpoint liefert { orders, has_more, next_cursor } — kein plain Array.
            const raw = await r.json();
            const orders = Array.isArray(raw) ? raw : (raw.orders || []);
            const hasMore = !Array.isArray(raw) && raw.has_more === true;
            data.ordersOpen   = orders.filter(o => o.status === "Offen").length;
            data.ordersReady  = orders.filter(o => o.status === "Bereit").length;
            data.ordersTotal  = orders.length;
            // Erste 2 offene Bestellungen (für die Versand-Card im Dashboard).
            const openOrders = orders.filter(o => o.status === "Offen" || o.status === "Bereit");
            data.openOrdersCount = openOrders.length;
            data.openOrdersFirst2 = openOrders.slice(0, 2).map(o => ({
              id: o.id,
              bestellnummer: o.bestellnummer,
              kanal: o.kanal,
              status: o.status,
              datum: o.datum,
              storeName: s.name,
            }));
            // Helfer-Modus-Vorschau: nur "Bereit" — konsistent mit data.ordersReady
            // (gleiche Quelle), damit Count und Liste nicht auseinanderlaufen.
            data.readyOrdersFirst2 = orders
              .filter(o => o.status === "Bereit")
              .slice(0, 2)
              .map(o => ({
                id: o.id,
                bestellnummer: o.bestellnummer,
                kanal: o.kanal,
                status: o.status,
                datum: o.datum,
                storeName: s.name,
              }));
            // Online-Store Umbau Phase 1: Umsatz pro Bestellung = Σ(menge × unit_price)
            // der gesnapshotteten order_items (vom /orders-Endpoint mitgeliefert).
            // Ersetzt die fixe 27,99-/Kanal-Preis-Logik.
            const orderRevenue = (o) => (Array.isArray(o.order_items) ? o.order_items : [])
              .reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
            // Gesamtumsatz + Wochen-Umsatz pro Store.
            // Retouren werden nicht gezählt.
            const weekStartDay = weekStartIso.slice(0, 10);
            let storeTotalRevenue = 0;
            let last30 = 0, prev30 = 0, oldestMs = Infinity;
            for (const o of orders) {
              if (o.status === "Retoure") continue;
              const price = orderRevenue(o);
              storeTotalRevenue += price;
              // Wochen-Umsatz: nur Orders der laufenden Woche
              const rawDate = o.status === "Versendet"
                ? (o.updated_at || o.datum || o.created_at)
                : (o.datum || o.created_at);
              const day = rawDate ? String(rawDate).slice(0, 10) : null;
              if (day && day >= weekStartDay) {
                weekRevenue.online += price;
              }
              const ms = rawDate ? new Date(rawDate).getTime() : NaN;
              const bi = weekBucket(ms);
              if (bi >= 0) weeks[bi].online += price;
              // Wachstums-Fenster
              if (Number.isFinite(ms)) {
                if (ms >= win30Ms) last30 += price;
                else if (ms >= win60Ms) prev30 += price;
                oldestMs = Math.min(oldestMs, ms);
              }
            }
            data.totalRevenue = storeTotalRevenue;
            const g = growth(last30, prev30, hasMore && oldestMs > win60Ms);
            data.growthPct = g.pct;
            data.growthReason = g.reason;
          }
        } else if (s.type === "flea") {
          // Sessions mit embedded sales (Worker-Endpoint, P-Polish-Refactor):
          // 1 Anfrage statt N+1 Detail-Calls.
          const sr = await fetch(`${apiBase}/sessions?include_stats=1&limit=2000`, { headers });
          if (sr.ok) {
            const rawSr = await sr.json();
            const sessions = Array.isArray(rawSr) ? rawSr : (rawSr.sessions || []);
            const sessionsHasMore = !Array.isArray(rawSr) && rawSr.has_more === true;
            data.sessionsCount = sessions.length;
            data.lastSession = sessions[0] || null;
            // Aktive Session (ends_at IS NULL) → Live-Card-Stats fürs Dashboard.
            // Geplante Märkte (metadata.planned) haben auch kein ends_at — sind aber nicht live
            // (GET /sessions/active schließt sie ebenfalls aus).
            const activeSess = sessions.find(x => !x.ends_at && x.metadata?.planned !== true) || null;
            if (activeSess) {
              const liveSales = (activeSess.sales || []).filter(x => !x.is_voided);
              data.liveSession = {
                id: activeSess.id,
                location: activeSess.location || "",
                starts_at: activeSess.starts_at || null,
                todayRevenue: liveSales.reduce((sum, x) => sum + Number(x.total || 0), 0),
                salesCount: liveSales.length,
              };
            }
            let storeTotalRevenue = 0;
            let last30 = 0, prev30 = 0, oldestMs = Infinity;
            for (const sess of sessions) {
              const sessionRevenue = (sess.sales || [])
                .filter(x => !x.is_voided)
                .reduce((sum, x) => sum + Number(x.total || 0), 0);
              storeTotalRevenue += sessionRevenue;
              // Wochen-Umsatz: Sessions die diese Woche begannen
              // (Approximation — die Embedded-Sales haben kein sold_at,
              // aber ein Markt-Tag ist typisch ≤ 1 Tag.)
              if (sess.starts_at && sess.starts_at >= weekStartIso) {
                weekRevenue[s.type] += sessionRevenue;
              }
              const ms = sess.starts_at ? new Date(sess.starts_at).getTime() : NaN;
              const bi = weekBucket(ms);
              if (bi >= 0) weeks[bi].flea += sessionRevenue;
              // Wachstums-Fenster (Bucketing über starts_at des Markttags)
              if (Number.isFinite(ms)) {
                if (ms >= win30Ms) last30 += sessionRevenue;
                else if (ms >= win60Ms) prev30 += sessionRevenue;
                oldestMs = Math.min(oldestMs, ms);
              }
            }
            data.totalRevenue = storeTotalRevenue;
            const g = growth(last30, prev30, sessionsHasMore && oldestMs > win60Ms);
            data.growthPct = g.pct;
            data.growthReason = g.reason;
          }
        }
      } catch (e) {
        console.warn("loadLandingData store failed:", s.id, e);
      }
      byStore[s.id] = data;
    }));

    const WEEK_LABELS = ["vor 3W", "vor 2W", "vor 1W", "Diese"];
    const weeksChart = weeks.map((w, i) => ({
      label: WEEK_LABELS[i],
      online_cents: Math.round(w.online * 100),
      troedel_cents: Math.round(w.flea * 100),
    }));

    setLandingData({ byStore, weekRevenue, weeks: weeksChart });
    setLoadingLanding(false);
  }


  // Lädt parallel die aktive Session (ends_at IS NULL) für jeden flea-Store.
  // Resultat in activeSessions Map: { [storeId]: sessionObj | null }.
  // Online-Stores werden übersprungen — die haben keine Sessions.
  async function loadActiveSessions(stores, token) {
    const modeStores = stores.filter(s => s.type === "flea");
    if (modeStores.length === 0) { setActiveSessions({}); return; }
    const t = token || authToken;
    const results = await Promise.all(modeStores.map(async (s) => {
      try {
        const r = await fetch(`/api/stores/${s.id}/sessions/active`, {
          headers: { "Authorization": `Bearer ${t}` },
        });
        if (!r.ok) return [s.id, null];
        return [s.id, await r.json()];
      } catch {
        return [s.id, null];
      }
    }));
    setActiveSessions(Object.fromEntries(results));
  }

  // P-Sheets-Skeleton: Re-Fetch beim Landing-Return.
  // Wenn der User vom Trödel-Hub zurück zur Landing kommt (z.B. nach
  // "Markt beenden"), müssen activeSessions + landingData reaktiv aktualisiert
  // werden. Sonst bleibt der "Markt LÄUFT"-Banner stehen bis App-Restart.
  // Triggered: jedes Mal wenn appScreen zu "dashboard" wechselt UND Stores
  // bereits geladen sind. Initialer Load wird durch loadUserStores abgedeckt
  // (ruft loadActive/loadLanding selbst), daher prüfen wir userStores.length > 0.
  useEffect(() => {
    if (appScreen !== "dashboard") return;
    if (!authToken || !userStores || userStores.length === 0) return;
    // Background-Refresh ohne Skeleton-Flicker
    loadActiveSessions(userStores, authToken);
    loadLandingData(userStores, authToken);
    // eslint-disable-next-line
  }, [appScreen]);

  // Mode-Store anlegen (Trödel).
  // Frontend-Pre-Check: User hat bereits einen Store dieses Typs → direkt
  // dorthin weiterleiten ohne API-Call.
  // Server-Side-Pre-Check (HTTP 409): Worker liefert den existierenden Store
  // zurück → Frontend nimmt ihn und ruft selectStore.
  async function handleCreateModeStore(type) {
    if (type !== "flea") return;
    const existing = userStores.find(s => s.type === type);
    if (existing) {
      selectStore(existing);
      return;
    }
    try {
      const res = await fetch("/api/stores", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          name: "Trödel",
          type,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data?.store) {
        // Server kennt einen existierenden Store → übernehmen
        const list = userStores.find(s => s.id === data.store.id) ? userStores : [...userStores, data.store];
        setUserStores(list);
        selectStore(data.store);
        return;
      }
      if (!res.ok) {
        toast.error(data?.reason || "Anlegen fehlgeschlagen");
        return;
      }
      // Erfolgreich angelegt → Liste neu laden, dann zum neuen Store
      const created = data;
      setUserStores(prev => [...prev, created]);
      selectStore(created);
    } catch (e) {
      toast.error("Netzwerkfehler beim Anlegen");
    }
  }

  // Generalisierte Store-Erstellung für das Create-Sheet.
  // Kein optimistic update — Full-Reload nach Erfolg.
  async function createStore(name, type) {
    const res = await fetch("/api/stores", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify({ name: name.trim(), type }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 409 && data?.store) return data.store;
    if (!res.ok) throw new Error(data?.reason || "Anlegen fehlgeschlagen");
    await loadUserStores(authToken);
    return data;
  }

  // Bereich per Einladungscode beitreten (Helfer-Flow).
  // Gleicher Full-Reload-Pfad wie createStore — Single Source of Truth.
  async function joinStore(code) {
    const res = await fetch(`/api/invitations/${encodeURIComponent(code)}/redeem`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.reason || data?.error || "Code ungültig oder abgelaufen");
    await loadUserStores(authToken);
    return data; // { store, role, already_member? }
  }

  function selectStore(storeWithRole, token, options = {}) {
    setCurrentStore(storeWithRole);
    setCurrentRole(storeWithRole.role);
    const mode = storeWithRole.type === "flea" ? "flea" : "online";
    setCurrentMode(mode);
    if (mode === "flea") {
      setOpenLiveDirectly(!!options?.openLiveDirectly);
      navigate("flea-hub");
      return;
    }
    navigate("app");
  }


  // ── Stale-Session-Reminder (P-Polish 1.1) ────────────────
  // Wenn eine Trödel-Session > 12h aktiv ist, zeige ein Reminder-Modal
  // beim nächsten App-Open. Dismissed-State pro session.id (in-memory).
  const staleSession = (() => {
    if (!activeSessions || !userStores) return null;
    for (const [storeId, session] of Object.entries(activeSessions)) {
      if (!session || session.ends_at) continue;
      if (staleSessionDismissed.has(session.id)) continue;
      const hours = (Date.now() - new Date(session.starts_at).getTime()) / 3600000;
      if (hours < 12) continue;
      const store = userStores.find(s => s.id === storeId);
      if (!store) continue;
      return { store, session };
    }
    return null;
  })();
  const staleReminderJsx = staleSession ? (
    <StaleSessionReminderSheet
      dark={dark} t={t}
      store={staleSession.store}
      session={staleSession.session}
      onDismiss={() => {
        setStaleSessionDismissed(prev => new Set(prev).add(staleSession.session.id));
      }}
      onEndNow={() => {
        // User in den Store navigieren — dort kann er „Markt beenden" tippen.
        // (Suggested ends_at = letzter Verkauf + 30 Min wäre nice-to-have, kommt
        // später als Optimierung im Sheet selbst.)
        setStaleSessionDismissed(prev => new Set(prev).add(staleSession.session.id));
        selectStore(staleSession.store);
        toast.show({ variant: 'info', title: `Tippe „Markt beenden" um den Tagesabschluss zu öffnen`, duration: 3500 });
      }}
    />
  ) : null;

  // ── Onboarding screens (rendered before the main app) ────
  if (appScreen === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: dark ? "#0d1117" : "#dde8f8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F, color: dark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", fontSize: 14 }}>
        Lade…
      </div>
    );
  }
  if (appScreen === "auth") {
    return <AuthScreen onAuth={(session) => {
      setAuthToken(session.access_token);
      setAuthUser(session.user);
      loadUserStores(session.access_token);
    }} />;
  }
  if (appScreen === "dashboard") {
    return <ConfirmProvider dark={dark}><>
      <AppShell
        initialTab="dashboard"
        authToken={authToken}
        authUser={authUser}
        currentRole={currentRole}
        dark={dark}
        navigate={navigate}
        userStores={userStores}
        landingData={landingData}
        activeSessions={activeSessions}
        userPreferences={userPreferences}
        onPreferencesChange={saveUserPreferences}
        onSelectStore={(s, opts) => selectStore(s, undefined, opts)}
        onCreateStore={createStore}
        onJoinStore={joinStore}
        themeMode={themeMode}
        onThemeModeChange={setThemeMode}
        onLogout={async () => {
          if (supabase) await supabase.auth.signOut();
          setAppScreen("auth");
        }}
        onRefresh={() => { loadLandingData(userStores, authToken); loadActiveSessions(userStores, authToken); }}
      />
      {createAreaOpen && (
        <CreateAreaSheet
          dark={dark} t={t}
          userStores={userStores}
          authToken={authToken}
          onClose={() => setCreateAreaOpen(false)}
          onCreateOnline={() => { setCreateAreaOpen(false); navigate("create-store"); }}
          onCreateMode={(type) => { setCreateAreaOpen(false); handleCreateModeStore(type); }}
          onSelectExisting={(store) => { setCreateAreaOpen(false); selectStore(store); }}
          onJoinedViaCode={async (joinedStore) => {
            setCreateAreaOpen(false);
            if (authToken) await loadUserStores(authToken);
            if (joinedStore) selectStore(joinedStore);
          }}
        />
      )}
      {staleReminderJsx}
    </></ConfirmProvider>;
  }
  if (appScreen === "create-store") {
    return <div ref={swipeRef} style={{ position: 'fixed', inset: 0, ...swipeStyle }}>
      <CreateStoreScreen
        token={authToken}
        onCreated={(store) => {
          setUserStores(prev => [...prev, store]);
          selectStore(store);
        }}
        onBack={goBack}
      />
    </div>;
  }
  if (appScreen === "join-store") {
    return <div ref={swipeRef} style={{ position: 'fixed', inset: 0, ...swipeStyle }}>
      <JoinStoreScreen
        token={authToken}
        onJoined={(store) => {
          setUserStores(prev => prev.find(s => s.id === store.id) ? prev : [...prev, store]);
          selectStore(store);
        }}
        onBack={goBack}
      />
    </div>;
  }
  if (appScreen === "flea-hub") {
    if (Troedel) {
      return <ConfirmProvider dark={dark}><div ref={swipeRef} style={{ position: 'fixed', inset: 0, ...swipeStyle }}>
        <ErrorBoundary fallbackLabel="Trödel-Modus" onReset={() => setAppScreen("dashboard")}>
          <>
            <Troedel
              store={currentStore}
              authToken={authToken}
              openLiveDirectly={openLiveDirectly}
              onBack={() => { setOpenLiveDirectly(false); goBack(); }}
              onStoreDeleted={() => {
                const sid = currentStore?.id;
                setUserStores(prev => prev.filter(s => s.id !== sid));
                setCurrentStore(null);
                setCurrentRole(null);
                setCurrentMode("online");
                setAppScreen("dashboard");
              }}
              dark={dark}
              userPreferences={userPreferences}
              saveUserPreferences={saveUserPreferences}
            />
            {staleReminderJsx}
      
          </>
        </ErrorBoundary>
      </div></ConfirmProvider>;
    }
    return <ModeHubStub
      title="Trödel"
      subtitle="Lade Trödel-Modul…"
      accentColor={{ weak: "rgba(127,119,221,0.12)", solid: "var(--accent-troedel)" }}
      onBack={goBack}
    />;
  }
  // ── Default: Online store ────────────────────────────────
  return (
    <ConfirmProvider dark={dark}>
      <div ref={swipeRef} style={{ position: 'fixed', inset: 0, ...swipeStyle }}>
        <ErrorBoundary fallbackLabel="Online-Store" onReset={() => setAppScreen("dashboard")}>
          <OnlineStore
            currentStore={currentStore}
            currentRole={currentRole}
            authToken={authToken}
            authUser={authUser}
            dark={dark}
            setDark={(v) => setThemeMode(v ? 'dark' : 'light')}
            onBack={goBack}
            onStoreDeleted={(storeId) => {
              setUserStores(prev => prev.filter(s => s.id !== storeId));
              setCurrentStore(null);
              setCurrentRole(null);
              setCurrentMode("online");
              setAppScreen("dashboard");
            }}
            userStores={userStores}
          />
        </ErrorBoundary>
      </div>
    </ConfirmProvider>
  );
}

// ─────────────────────────────────────────────────────────────
export default App;
